'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// A microphone check: getUserMedia + AnalyserNode level meter. The audio graph
// runs entirely in the page; nothing is recorded and nothing leaves the device,
// which is the whole reason to use a mic test on a website you trust.

export default function BrowserMicTest() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const contextRef = useRef<AudioContext | null>(null);

  const [state, setState] = useState<'idle' | 'live' | 'denied' | 'error'>('idle');
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null);
  const [peak, setPeak] = useState(0);

  useEffect(() => {
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stop() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void contextRef.current?.close().catch(() => undefined);
    contextRef.current = null;
    setState('idle');
    setDeviceLabel(null);
  }

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const track = stream.getAudioTracks()[0];
      setDeviceLabel(track?.label || 'Microphone');
      setState('live');
      void consumeToolUsage('mic-test', '').catch(() => undefined);

      const audioContext = new AudioContext();
      contextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      let sessionPeak = 0;
      const draw = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');
        if (!context) return;

        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (const value of data) {
          const centered = (value - 128) / 128;
          sum += centered * centered;
        }
        const rms = Math.sqrt(sum / data.length);
        const level = Math.min(1, rms * 3);
        sessionPeak = Math.max(sessionPeak, level);
        setPeak(sessionPeak);

        const { width, height } = canvas;
        context.clearRect(0, 0, width, height);
        context.fillStyle = '#1c2230';
        context.fillRect(0, 0, width, height);
        const barWidth = width * level;
        const gradient = context.createLinearGradient(0, 0, width, 0);
        gradient.addColorStop(0, '#1baf7a');
        gradient.addColorStop(0.7, '#eda100');
        gradient.addColorStop(1, '#e34948');
        context.fillStyle = gradient;
        context.fillRect(0, 0, barWidth, height);
        // Peak marker
        context.fillStyle = '#ffffff';
        context.fillRect(Math.max(0, width * sessionPeak - 2), 0, 2, height);

        rafRef.current = requestAnimationFrame(draw);
      };
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = 600;
        canvas.height = 48;
      }
      draw();
    } catch (error) {
      setState(
        error instanceof DOMException && error.name === 'NotAllowedError' ? 'denied' : 'error',
      );
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Test your microphone</h2>
          <p>
            Press Start, allow microphone access, and speak: the meter moves if the mic works.
            Nothing is recorded and no audio leaves your device.
          </p>
        </div>
        <span className="tool-live-badge">Mic</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <canvas
            aria-label="Microphone level meter"
            className="tool-image-preview-canvas"
            ref={canvasRef}
            style={{ borderRadius: 10, height: 48, width: '100%' }}
          />
          {state === 'live' ? (
            <p aria-live="polite" className="tool-live-panel-note">
              Listening to <strong>{deviceLabel}</strong>. Speak normally: a healthy mic pushes the
              bar into the yellow on speech. Session peak: {(peak * 100).toFixed(0)}%.
            </p>
          ) : null}
          {state === 'denied' ? (
            <p className="support-feedback support-feedback-error">
              Microphone access was blocked. Allow it in the address-bar permission prompt, then
              press Start again. The audio still never leaves this page.
            </p>
          ) : null}
          {state === 'error' ? (
            <p className="support-feedback support-feedback-error">
              No microphone was found, or the browser could not open it. Check that a mic is
              connected and not in use by another app.
            </p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <p className="tool-live-panel-note">
            What the meter tells you: a moving bar means the browser hears the mic. A dead bar with
            access granted means the wrong input device is selected in your system settings, the mic
            is muted in hardware, or another app holds it exclusively.
          </p>
        </aside>
      </div>

      <div className="hero-actions tool-live-actions">
        {state === 'live' ? (
          <button className="button button-ghost" onClick={stop} type="button">
            Stop the test
          </button>
        ) : (
          <button className="button" onClick={() => void start()} type="button">
            Start the mic test
          </button>
        )}
      </div>
    </article>
  );
}
